import { useEffect } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import { Button, DashboardModule } from '@domain-group/suit-theme'

import { useFilters } from '@domain-group/react-hooks'

import DashboardModuleFilters from '@components/DashboardModuleFilters'

import type { AgencyOrderBundleModel } from '@models/types'

import useAgencyOrderBundle from '@hooks/useAgencyOrderBundle'
import useAgencyOrderBundles from '@hooks/useAgencyOrderBundles'
import useSelectEntities from '@hooks/useSelectEntities'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

import { confirmDelete } from '@functions/entity'

import OrderBundleAgencyListItem from './OrderBundleAgencyListItem'

const buildPageFilterFields = () => {
  const style = {
    marginBottom: 16,
  }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'agency_string',
      label: 'Filter by Agency Title',
    },
  ]
}

const OrderBundleAgencies = (props: {orderBundleId: number}) => {
  const { orderBundleId } = props

  const filterPayload = useFilters()
  const {
    pageFilters,
  } = filterPayload

  const agencyOrderBundlesPayload = useAgencyOrderBundles({
    filters: {
      order_bundle_id: orderBundleId,
      ...pageFilters,
    },
    performHttpRequests: !!orderBundleId,
    requestOptions: defaultRequestOptions.agencyOrderBundle,
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAgencyOrderBundles,
    filteredAgencyOrderBundlesCount,
    hasFilteredAgencyOrderBundles,
    loading,
  } = agencyOrderBundlesPayload

  const {
    callbacks: { bulkCreate, bulkDeleteAgencyOrderBundles },
  } = useAgencyOrderBundle({ order_bundle_id: orderBundleId })

  const unselectEntitiesPayload = useSelectEntities()
  const {
    callbacks: {
      getSelectableListItemProps,
      deselectAll,
    },
    hasMinimumSelected,
    selectedEntitiesCount,
    selectedEntityIds,
  } = unselectEntitiesPayload

  const confirmDeleteParams = {
    callbacks: {
      afterAction: deselectAll,
      buildActionParams: () => (
        [{ agency_order_bundle_ids: selectedEntityIds, order_bundle_id: orderBundleId }]
      ),
      deleteFn: bulkDeleteAgencyOrderBundles,
    },
    options: { isBulkDelete: true },
    strings: {
      deleteString: 'unassign',
      entityName: 'Agencies',
    },
  }

  useEffect(() => {
    deselectAll()
  }, [filteredAgencyOrderBundlesCount])

  return (
    <DashboardModule
      contentBoxProps={{ flexDirection: 'column' }}
      headerRight={hasFilteredAgencyOrderBundles && <DashboardModule.FilterHeader {...filterPayload} />}
      loading={loading}
      title="Agencies"
    >
      <DashboardModuleFilters
        filterFields={buildPageFilterFields()}
        {...filterPayload}
      />

      {!loading && !hasFilteredAgencyOrderBundles && (
        <DashboardModule.BlankState>
          <DashboardModule.BlankState.Title>
            No Agencies present.
          </DashboardModule.BlankState.Title>

          <DashboardModule.BlankState.Paragraph>
            Please adjust your filters or add an Agency.
          </DashboardModule.BlankState.Paragraph>
        </DashboardModule.BlankState>
      )}

      {!loading && hasFilteredAgencyOrderBundles && (
        // @ts-expect-error TS2322
        <DashboardModule.ScrollableSection flexDirection="column">
          {filteredAgencyOrderBundles.map((agencyOrderBundle: AgencyOrderBundleModel) => {
            const { id } = agencyOrderBundle
            const listItemProps = getSelectableListItemProps(id)

            return (
              <OrderBundleAgencyListItem
                agencyOrderBundle={agencyOrderBundle}
                key={id}
                {...listItemProps}
              />
            )
          })}
        </DashboardModule.ScrollableSection>
      )}

      <DashboardModule.LoadMoreFooter
        boxProps={{ borderTop: loading ? 'none' : '1px solid' }}
        callbacks={{ loadMore }}
        canLoadMore={canLoadMore}
        entityCount={filteredAgencyOrderBundlesCount}
        loading={loading}
      >
        {hasMinimumSelected && (
          <Button
            buttonStyle="secondaryEdit"
            icon={<FontAwesomeIcon icon={faTrashAlt} />}
            marginRight={[0, 'medium']}
            marginTop={['medium', 0]}
            // @ts-expect-error TS2345
            onClick={() => confirmDelete(confirmDeleteParams)}
            // @ts-expect-error TS2322
            size={['medium', 'small']}
            width={['100%', 'fit-content']}
          >
            Unassign Selected Agencies ({selectedEntitiesCount})
          </Button>
        )}

        <Button
          buttonStyle="secondaryEdit"
          icon={<FontAwesomeIcon icon={faPlus} />}
          marginTop={hasFilteredAgencyOrderBundles ? ['medium', 0] : 0}
          onClick={() => bulkCreate()}
          // @ts-expect-error TS2322
          size={['medium', 'small']}
          width={['100%', 'fit-content']}
        >
          Add Agencies
        </Button>
      </DashboardModule.LoadMoreFooter>
    </DashboardModule>
  )
}

export default OrderBundleAgencies
